<script lang="ts">
  import type { Snippet } from "svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiArrowRight, mdiCheckCircleOutline } from "@mdi/js"
  import type { GuideCourse } from "../../../../../../../../api/kehrblatt/learning-guide"
  import { spaLink } from "../../../../../../../../_shared/_logic/navigation"
  import {
    getLearningPathProgress,
    getLearningLessonHref,
    isLearningSetupPath,
  } from "../../../../../../_shared/_logic/learning-path"

  const {
    course,
    basePath,
    search = "",
    chapterSearch,
    taskShortcuts,
    searching = false,
  } = $props<{
    course: GuideCourse
    basePath: string
    search?: string
    chapterSearch?: Snippet
    taskShortcuts?: Snippet
    searching?: boolean
  }>()
  const setup = $derived(isLearningSetupPath(search))
  const progress = $derived(getLearningPathProgress(course.lessons, search))
  const nextLesson = $derived(progress.nextLesson)
  const titleId = $props.id()
</script>

<section class="course-overview" aria-labelledby={titleId}>
  <header class="course-heading">
    <h1 id={titleId}>{setup ? "Ihr Konto einrichten" : "Ihr Lernpfad"}</h1>
    <p class="progress-label"
      >{course.completed_count} von {course.total_count} Anleitungen erledigt</p
    >
    <progress
      max={Math.max(course.total_count, 1)}
      value={course.completed_count}
      aria-label="Gesamter Lernfortschritt"
    ></progress>
  </header>
  {#if nextLesson}
    <div class="next-chapter">
      <div class="next-chapter-title">
        <p class="next-label">Als Nächstes</p>
        <h2>{nextLesson.title}</h2>
      </div>
      <a class="continue" href={getLearningLessonHref(basePath, nextLesson, search)} use:spaLink>
        <Icon path={mdiArrowRight} size="1.15rem" />{course.completed_count
          ? "Weiterlernen"
          : setup
            ? "Mit dem Import beginnen"
            : "Mit Kapitel 1 beginnen"}
      </a>
    </div>
  {:else}
    <p class="goal-done"
      ><Icon path={mdiCheckCircleOutline} size="1.15rem" />{setup
        ? "Sie haben die Anleitungen zur Einrichtung abgeschlossen."
        : "Sie haben alle Anleitungen abgeschlossen."}</p
    >
  {/if}
  {#if !setup}
    <p class="setup-hint"
      >Ihr Konto ist noch nicht eingerichtet? <a
        href={`${basePath}?lernweg=einrichtung`}
        use:spaLink>Hier starten.</a
      ></p
    >
  {:else}
    <p class="setup-hint"><a href={basePath} use:spaLink>Zum gesamten Lernpfad</a></p>
  {/if}
  <section class="chapter-directory" aria-labelledby={`${titleId}-directory`}>
    <h2 id={`${titleId}-directory`}>Was möchten Sie erledigen?</h2>
    {#if chapterSearch}<div class="directory-search">{@render chapterSearch()}</div>{/if}
    {#if taskShortcuts && !searching}{@render taskShortcuts()}{/if}
  </section>
</section>

<style>
  .course-overview {
    min-width: 0;
    color: inherit;
  }
  .course-heading {
    display: grid;
    gap: 0.85rem;
  }
  h1 {
    margin: 0;
    font-size: 28px;
    color: inherit;
    line-height: 1.25;
  }
  h2 {
    margin: 0;
    font-size: 21px;
    color: inherit;
    line-height: 1.35;
  }
  p {
    margin: 0 0 0.75rem;
    line-height: 1.5;
    max-width: 72ch;
  }
  .progress-label {
    margin: 0;
    color: #526974;
    font-size: 16px;
  }
  .setup-hint a {
    color: inherit;
  }
  .setup-hint {
    margin-bottom: 1.75rem;
    font-size: 16px;
  }
  .next-chapter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem 2rem;
    margin: 1.5rem 0 1rem;
    padding: 1.25rem 1.5rem;
    border-left: 4px solid #7c2024;
    background: #f8f3f3;
  }
  .next-chapter-title {
    flex: 1 1 24rem;
    min-width: 0;
  }
  .next-label {
    margin-bottom: 0.35rem;
    font-size: 16px;
    color: #526974;
  }
  .chapter-directory > h2 {
    margin-bottom: 1rem;
  }
  .directory-search {
    margin-bottom: 1.5rem;
  }
  .goal-done {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #246b43;
    margin-top: 0.75rem;
  }
  progress {
    appearance: none;
    display: block;
    width: 100%;
    height: 0.45rem;
    border: 0;
    background: #e8ecee;
    color: #28794a;
  }
  progress::-webkit-progress-bar {
    background: #e8ecee;
  }
  progress::-webkit-progress-value {
    background: #28794a;
  }
  progress::-moz-progress-bar {
    background: #28794a;
  }
  .continue {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.65rem 0.85rem;
    background: #7c2024;
    color: #fff;
    font-weight: 600;
    text-decoration: none;
    min-height: 44px;
    box-sizing: border-box;
    line-height: 1.4;
  }
  a:focus-visible {
    outline: 3px solid #344e5a;
    outline-offset: 3px;
  }
  h1,
  h2,
  a {
    overflow-wrap: anywhere;
  }
  @media (max-width: 600px) {
    .next-chapter {
      padding: 1rem;
    }
    .continue {
      width: 100%;
    }
  }
</style>
